/* eslint-disable */
import { useState, useEffect, useMemo } from "react";
import { OLAPTransformerResult, useParameter, useDictionary, Column } from "../../../shared";
import Highcharts, { SeriesOptionsType } from "highcharts";
import { useChartOptions } from "../../../../charts/useChartOptions";
import { legendPipe, chartPipe, tooltipPipe, addXAxisPipe, addYAxisPipe } from "../../../../charts/pipes";
import { dictionaryService } from "../../../dataFlow";
import { arrayByLength, uniq } from "../../../../../shared";
import { groupBy, last, orderBy, uniqBy } from "lodash";

const CATEGORIES_AXIS = "calendar.default";
const SERIES_AXIS = "commodities.default";
const YEAR_FREQUENCY = 1;

export function useHighchartHistoricalTransformer(olapData: OLAPTransformerResult | null): Highcharts.Options | undefined {
  const [state, setState] = useState<Highcharts.Options>();
  const [calendarParameter] = useParameter<number[]>("@calendar");
  const [calendarItems] = useDictionary(dictionaryService, "calendar-origin");
  
  const olapColumns = useMemo(() => {
    const allColumns = [olapData].filter(Boolean).flatMap(e => e?.columns);
    const uniqColumns = uniqBy(allColumns, e => e?.selection[CATEGORIES_AXIS]).filter(Boolean);
    const primarySortedColumns = orderBy(uniqColumns, e => e?.selection[CATEGORIES_AXIS]);
    const secondarySortedColumns = orderBy(primarySortedColumns, e => String(e?.selection[CATEGORIES_AXIS]).slice(1, 5));
    return secondarySortedColumns as Column[];
  }, [olapData]);

  const [yearsCategories] = useMemo(() =>
    [YEAR_FREQUENCY]
      .map(frequency => {
        const frequencyColumns = olapColumns.filter(colum => {
          const colFrequency = +String(colum.selection[CATEGORIES_AXIS])[0];
          return colFrequency === frequency;
        });
        const frequencyCalendarIds = calendarParameter?.filter(calendarId => {
          const calendarFrequency = Number(String(calendarId)[0]);
          return calendarFrequency === frequency;
        });
        const ignoreCalendarIds = frequencyCalendarIds?.filter(x => !frequencyColumns.some(e => x === (e?.selection[CATEGORIES_AXIS] as number))) || [];

        return frequencyCalendarIds?.filter(calendarId => !ignoreCalendarIds.includes(calendarId)).map(calendarId => {
          const calendarItem = calendarItems?.find(item => item.id === String(calendarId));
          const name = calendarItem?.getValue<string>("name") || '';
          return {
            calendarId, name
          };
        }) || []
      })
    , [olapColumns, calendarItems, calendarParameter]
  );

  const allRows = [olapData].flatMap(data => data?.rows);
  const allPoints = allRows.flatMap(row => row?.points).filter(Boolean);
  const maxPointValue = Math.max(...allPoints.map(p => p!.value));
  const yMax = Math.ceil(maxPointValue / 100) * 100;

  const options = useChartOptions({
    tooltip: {
      useHTML: true,
      formatter: function () {
        const { category, serieName, unit } = this.point as any;
        const pointValue = Highcharts.numberFormat(this.point.y || 0, 0);
        return `<div>
            <b>${serieName || this.point.series.name}</b><br/>
            <b>${category}</b>: <span>${pointValue} ${unit}</span>
        </div>`;
      },
      outside: true,
      style: {
        zIndex: 100,
      },
    },
  }, [
    chartPipe(),
    legendPipe("bottom", {
      useHTML: true,
      labelFormatter: function () {
        const { name } = this;
        return name;
      }
    }),
    addXAxisPipe(yearsCategories.map(category => category.name), {
      visible: true,
      labels: {
        enabled: true,
        formatter: function () {
          const label = this.axis.defaultLabelFormatter.call(this);
          return label;
        }
      },

    }),
    addYAxisPipe({
      max: yMax,
      gridLineColor: '#fff',
      title: {
        text: "",
      },
      labels: {
        enabled: true,
        formatter: function () {
          const label = this.axis.defaultLabelFormatter.call(this);
          if (/^[0-9]{4}$/.test(label)) {
            return Highcharts.numberFormat(this.value, 0);
          }
          return label;
        },
      },
    }),
  ]);

  useEffect(() => {
    if (olapData) {
      const colors = ['#1CA955', '#E38114', '#DD5C3D', '#8F8F8F', '#D9D10C', '#2D5EDA', '#784FBA', '#D656A3'];
      const commoditiesSeries: SeriesOptionsType[] = olapData.rows.map((row, rowIndex) => {
        let commodityName = row.attributes[`${SERIES_AXIS}.name`];

        if (typeof commodityName === 'object') {
          commodityName = commodityName[9] || [];
        }

        const serieName = `${commodityName}`;
        return {
          name: `${commodityName} (USD/Metric Ton)`,
          data: yearsCategories?.map((category, categoryIndex) => {
            const point = row.points.find(p => p.column.selection[CATEGORIES_AXIS] === category.calendarId);
            const value = point?.value || null;
            return { x: categoryIndex, y: value, date: category.name, serieName, unit: 'USD/Metric Ton' };
          }) || [],
          yAxis: 0,
          xAxis: 0,
          color: colors[rowIndex],
          zIndex: 2,
          marker: {
            radius: 0,
          },
          showInLegend: true,
          states: {
            hover: {
              lineWidth: 0,
              lineWidthPlus: 0,
            },
          },
          dataLabels: {
            enabled: false,
          }
        } as any
      });

      const series: SeriesOptionsType[] = commoditiesSeries;
      const result: Highcharts.Options = {
        ...options,
        series,
      };
      setState(result);
    } else {
      setState(undefined);
    }
  }, [olapData, olapColumns, yearsCategories, calendarItems]);
  return state;
}
